import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const MusicSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const tracks = data?.tracks || [];
  const [playingTrack, setPlayingTrack] = useState(null);
  
  // Check if there's any music content
  const hasSpotifyEmbed = data?.spotify_embed_url || data?.spotify_embed_code;
  const hasSoundcloudEmbed = data?.soundcloud_embed_url || data?.soundcloud_embed_code;
  const hasPlatformLinks = data?.spotify_artist_url || data?.apple_music_url || data?.youtube_music_url || data?.soundcloud_url;
  const hasTracks = tracks.length > 0;
  
  if (!hasSpotifyEmbed && !hasSoundcloudEmbed && !hasPlatformLinks && !hasTracks) return null;

  // Platform icons
  const platformIcons = {
    spotify: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
      </svg>
    ),
    apple: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.785-.56-2.075-1.46-.18-.556-.14-1.116.108-1.646.305-.65.822-1.04 1.49-1.25.506-.156 1.026-.246 1.55-.33.315-.05.63-.09.938-.17.158-.04.295-.11.315-.3.008-.063.013-.126.013-.19V7.35c0-.113-.022-.16-.14-.146-.36.047-.72.086-1.077.14l-3.033.44c-.03.004-.063.017-.093.017-.12.01-.178.063-.19.185-.003.03-.004.06-.004.092v7.63c0 .41-.052.815-.23 1.19-.283.6-.76.984-1.397 1.168-.334.097-.676.152-1.022.167-1.04.045-1.9-.6-2.166-1.57-.147-.53-.1-1.058.155-1.55.317-.61.834-.983 1.483-1.178.5-.15 1.013-.237 1.53-.318.32-.05.64-.098.952-.177.164-.042.307-.117.325-.31.006-.05.008-.1.008-.15V5.594c0-.083.01-.167.03-.248.043-.18.165-.28.35-.306.075-.01.15-.018.225-.028l5.175-.75c.203-.03.407-.054.61-.083.103-.015.21-.028.312-.042.154-.02.24.03.287.186.023.077.034.16.034.24v5.545z"/>
      </svg>
    ),
    youtube: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    soundcloud: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.052-.1-.084-.1zm-.899 1.02c-.009.058-.009.058-.009.117l.139 1.137-.139 1.117c0 .059.009.117.009.117.034.058.076.058.118.058.042 0 .076-.017.118-.058.008-.017.008-.058.008-.117l.158-1.117-.158-1.137c0-.059 0-.117-.008-.117a.138.138 0 00-.118-.058c-.042 0-.084.017-.118.058zm1.655-.646c-.059 0-.109.05-.117.109l-.214 1.772.214 1.713c.008.059.058.109.117.109.059 0 .109-.05.118-.109l.243-1.713-.243-1.772c-.009-.059-.059-.109-.118-.109zm.764-.025c-.067 0-.117.05-.126.117l-.197 1.806.197 1.756c.009.067.059.117.126.117.067 0 .117-.05.126-.117l.218-1.756-.218-1.806c-.009-.067-.059-.117-.126-.117zm.782-.025c-.076 0-.134.059-.143.134l-.181 1.831.181 1.78c.009.076.067.135.143.135.075 0 .133-.059.142-.135l.205-1.78-.205-1.831c-.009-.075-.067-.134-.142-.134zm.781-.025c-.084 0-.15.067-.159.151l-.163 1.856.163 1.806c.009.084.075.151.159.151.083 0 .15-.067.159-.151l.184-1.806-.184-1.856c-.009-.084-.076-.151-.159-.151zm.782-.025c-.092 0-.167.075-.176.167l-.147 1.881.147 1.831c.009.092.084.167.176.167.092 0 .167-.075.176-.167l.168-1.831-.168-1.881c-.009-.092-.084-.167-.176-.167zm.782-.025c-.1 0-.184.084-.193.184l-.13 1.906.13 1.856c.009.101.093.185.193.185.101 0 .185-.084.194-.185l.151-1.856-.151-1.906c-.009-.1-.093-.184-.194-.184zm.781-.025c-.109 0-.193.092-.201.201l-.114 1.931.114 1.88c.008.11.092.202.201.202.109 0 .193-.092.202-.202l.134-1.88-.134-1.931c-.009-.109-.093-.201-.202-.201zm.782-.025c-.117 0-.21.1-.218.218l-.097 1.956.097 1.906c.008.118.101.218.218.218.118 0 .21-.1.218-.218l.118-1.906-.118-1.956c-.008-.118-.1-.218-.218-.218zm.781-.025c-.126 0-.227.109-.236.236l-.08 1.981.08 1.931c.009.126.11.235.236.235.125 0 .226-.109.235-.235l.101-1.931-.101-1.981c-.009-.127-.11-.236-.235-.236zm.782-.025c-.134 0-.243.117-.252.252l-.063 2.006.063 1.956c.009.134.118.252.252.252.134 0 .244-.118.253-.252l.084-1.956-.084-2.006c-.009-.135-.119-.252-.253-.252zm6.254-.609c-.521 0-1.025.084-1.496.252a5.238 5.238 0 00-5.187-4.635c-.286 0-.58.025-.865.075-.109.017-.143.059-.143.126v9.396c0 .067.05.126.118.134.009 0 7.321.008 7.573.008 1.571 0 2.846-1.285 2.846-2.864-.001-1.58-1.276-2.492-2.846-2.492z"/>
      </svg>
    ),
    play: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z"/>
      </svg>
    ),
    pause: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
      </svg>
    )
  };

  // Handle audio playback
  const handlePlayPause = (trackId) => {
    const audioEl = document.getElementById(`audio-${trackId}`);
    if (audioEl) {
      if (playingTrack === trackId) {
        audioEl.pause();
        setPlayingTrack(null);
      } else {
        // Pause any currently playing track
        if (playingTrack) {
          const currentAudio = document.getElementById(`audio-${playingTrack}`);
          if (currentAudio) currentAudio.pause();
        }
        audioEl.play();
        setPlayingTrack(trackId);
      }
    }
  };

  return (
    <section id="music" className={cn("py-24 px-4", t.card)}>
      <div className="max-w-5xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>
            Listen
          </span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.title || 'Music'}
          </h2>
        </AnimatedSection>
        
        {/* Spotify Embed Player */}
        {data.spotify_embed_url && (
          <AnimatedSection animation="fadeUp" className="mb-12">
            <div className="rounded-xl overflow-hidden">
              <iframe
                src={data.spotify_embed_url}
                width="100%"
                height="352"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{ borderRadius: '12px' }}
              />
            </div>
          </AnimatedSection>
        )}

        {/* SoundCloud Embed Player */}
        {data.soundcloud_embed_url && (
          <AnimatedSection animation="fadeUp" className="mb-12">
            <div className="rounded-xl overflow-hidden">
              <iframe
                src={data.soundcloud_embed_url}
                width="100%"
                height="300"
                frameBorder="0"
                allow="autoplay"
                loading="lazy"
                style={{ borderRadius: '12px' }}
              />
            </div>
          </AnimatedSection>
        )}

        {/* Individual Tracks */}
        {hasTracks && (
          <AnimatedSection animation="fadeUp" className="mb-12">
            <div className="space-y-3">
              {tracks.map((track, idx) => {
                const hasAudio = !!track.audio_file;
                const hasStreamingLinks = track.spotify_url || track.apple_music_url || track.youtube_music_url || track.soundcloud_url;
                const isPlaying = playingTrack === track.id;
                
                return (
                  <div 
                    key={track.id || idx}
                    className={cn("flex items-center gap-4 p-4 rounded-xl border transition-all", t.bg, t.border, hasAudio && "hover:border-opacity-80")}
                  >
                    {/* Cover art or play button */}
                    <div 
                      className={cn(
                        "w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden",
                        hasAudio && "cursor-pointer hover:scale-105 transition-transform"
                      )}
                      style={{ backgroundColor: track.cover_image ? 'transparent' : primaryColor + '20' }}
                      onClick={() => hasAudio && handlePlayPause(track.id)}
                    >
                      {track.cover_image ? (
                        <div className="relative w-full h-full">
                          <img src={track.cover_image} alt={track.title} className="w-full h-full object-cover" />
                          {hasAudio && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                              <div className="text-white">
                                {isPlaying ? platformIcons.pause : platformIcons.play}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : hasAudio ? (
                        <div className="text-white" style={{ color: primaryColor }}>
                          {isPlaying ? platformIcons.pause : platformIcons.play}
                        </div>
                      ) : (
                        <Music className="w-6 h-6" style={{ color: primaryColor }} />
                      )}
                    </div>
                    
                    {/* Track info */}
                    <div className="flex-1 min-w-0">
                      <h4 className={cn("font-semibold truncate", t.text)}>{track.title}</h4>
                      {track.artist && <p className={cn("text-sm", t.textMuted)}>{track.artist}</p>}
                      
                      {/* Audio player (hidden but functional) */}
                      {hasAudio && (
                        <audio 
                          id={`audio-${track.id}`}
                          src={track.audio_file}
                          onEnded={() => setPlayingTrack(null)}
                          className="hidden"
                        />
                      )}
                    </div>
                    
                    {/* Streaming links */}
                    {hasStreamingLinks && (
                      <div className="flex items-center gap-2">
                        {track.spotify_url && (
                          <a href={track.spotify_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#1DB954] flex items-center justify-center text-white hover:scale-110 transition-transform">
                            {platformIcons.spotify}
                          </a>
                        )}
                        {track.apple_music_url && (
                          <a href={track.apple_music_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#FA243C] flex items-center justify-center text-white hover:scale-110 transition-transform">
                            {platformIcons.apple}
                          </a>
                        )}
                        {track.youtube_music_url && (
                          <a href={track.youtube_music_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#FF0000] flex items-center justify-center text-white hover:scale-110 transition-transform">
                            {platformIcons.youtube}
                          </a>
                        )}
                        {track.soundcloud_url && (
                          <a href={track.soundcloud_url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-[#FF5500] flex items-center justify-center text-white hover:scale-110 transition-transform">
                            {platformIcons.soundcloud}
                          </a>
                        )}
                      </div>
                    )}
                    
                    {/* Play button for tracks with audio but no streaming links */}
                    {hasAudio && !hasStreamingLinks && (
                      <button
                        onClick={() => handlePlayPause(track.id)}
                        className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-110"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <span className="text-white">
                          {isPlaying ? platformIcons.pause : platformIcons.play}
                        </span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </AnimatedSection>
        )}
        
        {/* Platform Links */}
        {hasPlatformLinks && (
          <div className="flex flex-wrap justify-center gap-4">
            {data.spotify_artist_url && (
              <a
                href={data.spotify_artist_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-transform hover:scale-105"
                style={{ backgroundColor: '#1DB954', color: 'white' }}
              >
                {platformIcons.spotify}
                Spotify
              </a>
            )}
            {data.apple_music_url && (
              <a
                href={data.apple_music_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-transform hover:scale-105"
                style={{ backgroundColor: '#FA243C', color: 'white' }}
              >
                {platformIcons.apple}
                Apple Music
              </a>
            )}
            {data.youtube_music_url && (
              <a
                href={data.youtube_music_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-transform hover:scale-105"
                style={{ backgroundColor: '#FF0000', color: 'white' }}
              >
                {platformIcons.youtube}
                YouTube Music
              </a>
            )}
            {data.soundcloud_url && (
              <a
                href={data.soundcloud_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-transform hover:scale-105"
                style={{ backgroundColor: '#FF5500', color: 'white' }}
              >
                {platformIcons.soundcloud}
                SoundCloud
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
};


